/**
 * Alignment, distribution, grouping, and ungrouping functions for the fabric.js canvas editor.
 * Operates on the active selection or a single active object.
 */

import { getCanvasInstance } from './canvasInstance'
import { useEditorStore } from '@/stores/useEditorStore'

type AlignmentH = 'left' | 'centerH' | 'right'
type AlignmentV = 'top' | 'centerV' | 'bottom'
type Alignment = AlignmentH | AlignmentV

/**
 * Finds the page rect object on the canvas.
 * Returns null if not found.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function findPageRect(canvas: any): any | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return canvas.getObjects().find((obj: any) => obj._isPageRect) ?? null
}

/**
 * Returns the effective width of an object accounting for scale.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function effectiveWidth(obj: any): number {
    return (obj.width ?? 0) * (obj.scaleX ?? 1)
}

/**
 * Returns the effective height of an object accounting for scale.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function effectiveHeight(obj: any): number {
    return (obj.height ?? 0) * (obj.scaleY ?? 1)
}

/**
 * Aligns objects relative to the page rect (single object) or to each other (multiple objects).
 *
 * Single object alignment is relative to the page rect boundaries.
 * Multiple object alignment uses the bounding box of the entire selection.
 */
export function alignObjects(alignment: Alignment): void {
    const canvas = getCanvasInstance()
    if (!canvas) return

    const pageRect = findPageRect(canvas)
    if (!pageRect) return

    const activeObject = canvas.getActiveObject()
    if (!activeObject) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const obj = activeObject as any
    const isMultiple = obj.type === 'activeselection'

    if (isMultiple) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const objects: any[] = obj.getObjects().filter((o: any) => !o._isPageRect)
        if (!objects.length) return

        // Bounding box of the entire selection
        const selLeft: number = obj.left ?? 0
        const selTop: number = obj.top ?? 0
        const selWidth: number = obj.width ?? 0
        const selHeight: number = obj.height ?? 0

        const pageLeft: number = pageRect.left ?? 0
        const pageTop: number = pageRect.top ?? 0
        const pageWidth: number = pageRect.width ?? 0
        const pageHeight: number = pageRect.height ?? 0

        // Relative offsets to apply — align the whole group to the page
        let deltaX = 0
        let deltaY = 0

        switch (alignment) {
            case 'left':
                deltaX = pageLeft - selLeft
                break
            case 'centerH':
                deltaX = pageLeft + pageWidth / 2 - (selLeft + selWidth / 2)
                break
            case 'right':
                deltaX = pageLeft + pageWidth - (selLeft + selWidth)
                break
            case 'top':
                deltaY = pageTop - selTop
                break
            case 'centerV':
                deltaY = pageTop + pageHeight / 2 - (selTop + selHeight / 2)
                break
            case 'bottom':
                deltaY = pageTop + pageHeight - (selTop + selHeight)
                break
        }

        obj.set({
            left: (obj.left ?? 0) + deltaX,
            top: (obj.top ?? 0) + deltaY,
        })
        obj.setCoords()
    } else {
        // Single object — align to page rect
        if (obj._isPageRect) return

        const pageLeft: number = pageRect.left ?? 0
        const pageTop: number = pageRect.top ?? 0
        const pageWidth: number = pageRect.width ?? 0
        const pageHeight: number = pageRect.height ?? 0

        switch (alignment) {
            case 'left':
                obj.set({ left: pageLeft })
                break
            case 'centerH':
                obj.set({ left: pageLeft + pageWidth / 2 - effectiveWidth(obj) / 2 })
                break
            case 'right':
                obj.set({ left: pageLeft + pageWidth - effectiveWidth(obj) })
                break
            case 'top':
                obj.set({ top: pageTop })
                break
            case 'centerV':
                obj.set({ top: pageTop + pageHeight / 2 - effectiveHeight(obj) / 2 })
                break
            case 'bottom':
                obj.set({ top: pageTop + pageHeight - effectiveHeight(obj) })
                break
        }

        obj.setCoords()
    }

    canvas.requestRenderAll()
    useEditorStore.getState().pushUndo(JSON.stringify(canvas.toJSON()))
}

/**
 * Distributes 3 or more selected objects evenly along the given axis.
 * Objects are sorted by their left (horizontal) or top (vertical) position.
 * The first and last objects remain fixed; intermediate objects are repositioned.
 */
export function distributeObjects(direction: 'horizontal' | 'vertical'): void {
    const canvas = getCanvasInstance()
    if (!canvas) return

    const activeObject = canvas.getActiveObject()
    if (!activeObject) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const obj = activeObject as any
    if (obj.type !== 'activeselection') return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const objects: any[] = obj.getObjects().filter((o: any) => !o._isPageRect)
    if (objects.length < 3) return

    if (direction === 'horizontal') {
        // Sort by left edge
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        objects.sort((a: any, b: any) => (a.left ?? 0) - (b.left ?? 0))

        const firstLeft: number = objects[0].left ?? 0
        const lastRight: number = (objects[objects.length - 1].left ?? 0) + effectiveWidth(objects[objects.length - 1])
        const totalWidth = objects.reduce((sum: number, o: unknown) => sum + effectiveWidth(o), 0)
        const gap = (lastRight - firstLeft - totalWidth) / (objects.length - 1)

        let cursor = firstLeft
        for (const item of objects) {
            item.set({ left: cursor })
            item.setCoords()
            cursor += effectiveWidth(item) + gap
        }
    } else {
        // Sort by top edge
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        objects.sort((a: any, b: any) => (a.top ?? 0) - (b.top ?? 0))

        const firstTop: number = objects[0].top ?? 0
        const lastBottom: number = (objects[objects.length - 1].top ?? 0) + effectiveHeight(objects[objects.length - 1])
        const totalHeight = objects.reduce((sum: number, o: unknown) => sum + effectiveHeight(o), 0)
        const gap = (lastBottom - firstTop - totalHeight) / (objects.length - 1)

        let cursor = firstTop
        for (const item of objects) {
            item.set({ top: cursor })
            item.setCoords()
            cursor += effectiveHeight(item) + gap
        }
    }

    // Refresh the active selection's bounding box
    obj.setCoords()
    canvas.requestRenderAll()
    useEditorStore.getState().pushUndo(JSON.stringify(canvas.toJSON()))
}

/**
 * Groups the currently selected objects into a single fabric.Group.
 * Skips the page rect. Replaces the active selection with the new group.
 */
export async function groupSelected(): Promise<void> {
    const canvas = getCanvasInstance()
    if (!canvas) return

    const activeObject = canvas.getActiveObject()
    if (!activeObject) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const obj = activeObject as any
    if (obj.type !== 'activeselection') return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toGroup: any[] = obj.getObjects().filter((o: any) => !o._isPageRect)
    if (toGroup.length < 2) return

    const { Group } = await import('fabric')

    canvas.discardActiveObject()
    toGroup.forEach((item: unknown) => canvas.remove(item))

    const group = new Group(toGroup)
    canvas.add(group)
    canvas.setActiveObject(group)
    canvas.requestRenderAll()

    useEditorStore.getState().pushUndo(JSON.stringify(canvas.toJSON()))
}

/**
 * Ungroups the currently selected fabric.Group into individual objects.
 * Adds each child back to the canvas and creates a new active selection.
 */
export async function ungroupSelected(): Promise<void> {
    const canvas = getCanvasInstance()
    if (!canvas) return

    const activeObject = canvas.getActiveObject()
    if (!activeObject) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const obj = activeObject as any
    if (obj.type !== 'group') return

    const { ActiveSelection } = await import('fabric')

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const items: any[] = obj.getObjects()

    canvas.remove(obj)

    // Restore world transform for each item
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items.forEach((item: any) => {
        canvas.add(item)
        item.setCoords()
    })

    const selection = new ActiveSelection(items, { canvas })
    canvas.setActiveObject(selection)
    canvas.requestRenderAll()

    useEditorStore.getState().pushUndo(JSON.stringify(canvas.toJSON()))
}
